import { useEffect, useReducer, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Typography } from '../../ui/Typography';
import { liveChoiceOptions, type ChoiceOption, type MessageChoices } from '../../../../lib/message-choices';
import { useTranslation } from '../../../../lib/i18n';
import { meetingJoinChipEventId, meetingJoinChipView, nextMeetingJoinChange } from '../../../../lib/meeting-join-chip';
import { MEETING_STATE_BADGE } from '../../../../lib/meeting-row-state';
import { useMeetingsChannelActions, useUpcomingMeeting } from '../../../../lib/meetings-channel';
import { useAgentName } from '../../../../lib/use-agent-name';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { CHAT_HOME_BI } from '../message-utils';

// Choice chips — a structured ask's options as pill buttons under the assistant
// body. Selection derives from the transcript (a USER row carrying a matching
// choiceAnswer), so an answered chip gets the SELECTED treatment — but every chip
// stays tappable: another tap is another turn, and free text is always valid. No
// handler (read-only surface) renders the pills inert.
// The one closed case: a SUPERSEDED ask — an older unanswered card the agent has since re-asked
// (`supersededChoiceAskIds`) — is handed no handler, so it draws inert with no selection and no status.
//
// The pill is the Figma `Symphony Button Round`, skin LIGHT (Figma 20312-113668), which maps 1:1 onto
// the DS Button's own Light skin — so Default and Hover come straight from the skin tokens and need no
// override:
//   • Default  — `--ds-fill-light-default` (#fff / dark-elevated), the frame's white fill.
//   • Hover    — `--ds-fill-light-hover` (#EAE9E9 / #5E5E5E), the frame's grey hover (web only; a tap
//                target with no hover on touch is the DS Button's own behaviour).
//   • Selected — the grey `--ds-fill-light-secondary-hover` (#EAE9E9 / #5E5E5E) fill + a light
//                `--ds-separator-default` (#D2D2D2 / #787878) hairline, replacing the old accent
//                ring/tint. This is the SAME token native uses (iOS/Android `separatorDefault`), so the
//                selected border matches across surfaces in BOTH modes — `--ds-border-light-hover` would
//                read #969696 in dark and break that parity. The grey fill is what tells Selected
//                (persistent) apart from the white Default; the hairline is a quiet edge.
// The one override the frame does NOT draw is the Default border: the frame sits the white pill on a grey
// chat surface, but THIS chat surface is white, so a borderless white pill would vanish into it — hence
// the light `--ds-border-light-default` hairline for definition (the same reason SuggestedTaskChips keeps
// an outline here). The phones draw the pill borderless, because their chat surface is the grey #F3F3F3
// the frame assumes. Stated as a deviation in native's ChoiceChips too.
//
// An option carrying `url` is a LINK chip (e.g. "Join this meeting"): it renders as the same pill as a
// real anchor that opens the URL in a new tab, same as the meeting-notes card's own link. It never calls
// `onSelect` — a tap sends nothing, so it never reads as answered and never gets the selected look.
// `liveChoiceOptions` drops anything past its `expiresAt` at render time; a row whose options all
// drop that way renders no chip row at all.
//
// The one exception is a meeting thread's "Join this meeting" chip, which `MeetingJoinChip` draws from
// the meeting's live row instead (see `lib/meeting-join-chip.ts`).
export function ChoiceChips({
  choices,
  selectedIds,
  onSelect,
}: {
  choices: MessageChoices;
  selectedIds: ReadonlySet<string>;
  onSelect?: (option: ChoiceOption) => void;
}) {
  const { t } = useTranslation();
  const options = liveChoiceOptions(choices.options);
  if (options.length === 0) return null;
  const joinEventId = options.length === 1 && options[0].url ? meetingJoinChipEventId(options[0].id) : undefined;
  if (joinEventId) return <MeetingJoinChip eventId={joinEventId} url={options[0].url!} label={options[0].label} />;
  return (
    <div role="group" aria-label={t('chat.choices.groupLabel')} style={CHIPS_ROW}>
      {options.map((option) =>
        option.url ? (
          <ChoiceLinkChip key={option.id} url={option.url} label={option.label} />
        ) : (
          <ChoiceChip key={option.id} option={option} selected={selectedIds.has(option.id)} onSelect={onSelect} />
        ),
      )}
    </div>
  );
}

// A LINK option renders as the same pill via a real anchor. The props are SPREAD (not passed as JSX
// literals) so `style` AND `dir` type-check: the href branch resolves to ButtonAsLink, which declares
// neither (the DS Button forwards both to its <a> at runtime) — and excess-property checks fire only on
// object literals, not spreads. Same pattern as SuggestedTaskChips' ChipButton. `dir="auto"` rides the
// ANCHOR (not just the label) so the inline-flex row — label + trailing external-link glyph — follows
// the label's own direction (an RTL label in an LTR app keeps the glyph on the correct side; see
// .claude/rules/orion-web-chat.md). It never reads as selected and sends nothing on tap.
function ChoiceLinkChip({ url, label, onClick }: { url: string; label: string; onClick?: () => void }) {
  const props = {
    variant: 'contained' as const,
    color: 'light' as const,
    size: 'medium' as const,
    href: url,
    external: true,
    target: '_blank',
    rel: 'noopener noreferrer',
    dir: 'auto',
    style: PILL,
    endIcon: <ExternalLink size={16} aria-hidden />,
    onClick,
  };
  return <Button {...props}>{label}</Button>;
}

// Before the meeting the chip sends her and opens nothing; from a minute before the start it is the link,
// and a tap sends her too when she is not coming. Beside it, her state as the Meetings list's own badge.
// With no live row (still loading, or past the week the list covers) it stays the plain link it always was.
function MeetingJoinChip({ eventId, url, label }: { eventId: string; url: string; label: string }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const agentName = useAgentName().short.trim();
  const { meeting } = useUpcomingMeeting(eventId);
  const { decide } = useMeetingsChannelActions();
  const [failed, setFailed] = useState(false);
  useRerenderAt(meeting ? nextMeetingJoinChange(meeting, Date.now()) : undefined);

  if (!meeting) {
    return (
      <div role="group" aria-label={t('chat.choices.groupLabel')} style={CHIPS_ROW}>
        <ChoiceLinkChip url={url} label={label} />
      </div>
    );
  }
  const view = meetingJoinChipView(meeting, Date.now());
  if (!view.chip && !view.line) return null;

  const tap = () => {
    logPageClickBi({ ...CHAT_HOME_BI, button_name: 'meeting-join' }, { additionalFields: { chip: view.chip ?? '', state: meeting.state } });
    if (!view.requestsJoin) return;
    setFailed(false);
    decide.mutate({ meeting, decision: 'join' }, { onError: () => setFailed(true) });
  };
  // The Meetings list's own `stateLabel` rule.
  const stateLabel =
    view.line === 'scheduled' && agentName
      ? t('connectors.meetings.state.scheduledBy', { agent: agentName })
      : view.line && t(`connectors.meetings.state.${view.line}`);

  return (
    <div role="group" aria-label={t('chat.choices.groupLabel')} style={JOIN_BLOCK}>
      {view.chip === 'open' && <ChoiceLinkChip url={url} label={label} onClick={tap} />}
      {view.chip === 'send' && (
        <Button variant="contained" color="light" size="medium" dir="auto" style={PILL} loading={decide.isLoading} onClick={tap}>
          {label}
        </Button>
      )}
      {failed ? (
        <Typography variant="p3" color="secondary" role="status" style={JOIN_ERROR}>
          {t('chat.meetingJoin.failed', { agent: agentName })}
        </Typography>
      ) : (
        view.line && <Badge role="status" variant={MEETING_STATE_BADGE[view.line]} label={stateLabel} />
      )}
    </div>
  );
}

// The chip changes at two instants with no new data — a minute before the start, and the end — so it
// re-renders itself then.
function useRerenderAt(at: number | undefined) {
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (at === undefined) return;
    const id = setTimeout(rerender, Math.max(0, at - Date.now()));
    return () => clearTimeout(id);
  }, [at]);
}

function ChoiceChip({
  option,
  selected,
  onSelect,
}: {
  option: ChoiceOption;
  selected: boolean;
  onSelect?: (option: ChoiceOption) => void;
}) {
  // Read-only (locked channel — an answer is a channel send, so the chips lock with the composer): keep
  // the enabled Light-skin visual (contained-light has no disabled colour, so `aria-disabled` only sets
  // cursor:not-allowed), but mark it `aria-disabled` and drop it from the tab order + the click handler
  // so a screen-reader user isn't told an inert chip is actionable. Mirrors the phones' locked pills.
  const inert = !onSelect;
  return (
    <Button
      variant="contained"
      color="light"
      size="medium"
      aria-pressed={selected}
      aria-disabled={inert || undefined}
      onClick={inert ? undefined : () => onSelect!(option)}
      tabIndex={inert ? -1 : undefined}
      dir="auto"
      style={{ ...PILL, ...(selected ? PILL_SELECTED : {}) }}
    >
      {option.label}
    </Button>
  );
}

const CHIPS_ROW: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8, // Figma Spacing/8 (between chips)
  marginTop: 20, // Figma Spacing/20 — gap from the assistant message to the chips row
  maxWidth: '100%',
};

// The join chip with its status badge on the same row, centred on the chip's height. A narrow pane
// wraps the badge under the chip.
const JOIN_BLOCK: React.CSSProperties = {
  ...CHIPS_ROW,
  alignItems: 'center',
  columnGap: 12,
};

// The failed-send sentence is not a state, so it keeps its own line under the chip.
const JOIN_ERROR: React.CSSProperties = { flexBasis: '100%' };

// The round pill: the DS Light skin supplies the white fill and the #EAE9E9 hover; we override only the
// radius and the definition border (see the header note on why this surface keeps one). The label wraps
// instead of the Button's `nowrap`: a 40-character label (the harness cap) is wider than a phone at a large
// text size. Same rule as the phones' chips.
const PILL: React.CSSProperties = {
  borderRadius: 9999,
  borderColor: 'var(--ds-border-light-default)',
  maxWidth: '100%',
  whiteSpace: 'normal',
  textAlign: 'center',
};

// Selected (Figma 20312-113671): the grey `Light Secondary/Hover` fill + the light `--ds-border-light-hover`
// (Border/light/Hover, #D2D2D2) hairline, in place of the old accent ring. The fill is set here (not left
// to the skin) so it stays grey whether or not the chip is hovered.
const PILL_SELECTED: React.CSSProperties = {
  background: 'var(--ds-fill-light-secondary-hover)',
  borderColor: 'var(--ds-separator-default)',
};
